import React, { useEffect, useRef, useState } from 'react'

import { ChevronLeftIcon, ChevronRightIcon } from './icons'
import { addDays, formatDate, monthGrid, monthTitle, nextMonth, todayIso, weekdayLabels } from './dateRange.utils'

const ARROW_STEP = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }

/**
 * Inline two-month range calendar (mock `.cev-cal`): prev / hint / next header, two month grids
 * (one under 640px, CSS), the "Clear dates" footer. Past days are disabled. The range band is the
 * cell background (`start / end / in`), the hover preview the `hov / hstart / hend` classes.
 * Keyboard: one day is reachable with Tab (roving tabindex), the arrows move by ±1 / ±7 days and
 * shift the view past its edges. `focusIso` + `focusKey` ask for a day to receive focus after
 * the parent redraws (opening, first click, month shift from the keyboard).
 */
export default function DateRangeCalendar({
    labelledBy,
    start,
    end,
    pick,
    view,
    hint,
    focusIso,
    focusKey,
    onShift,
    onChoose,
    onClear,
    language,
    t,
}) {
    const [hover, setHover] = useState('')
    const [active, setActive] = useState(focusIso || '')
    const dayRefs = useRef({})
    const today = todayIso()

    const months = [view, nextMonth(view)].map(({ year, month }) => ({
        key: `${year}-${month}`,
        title: monthTitle(year, month, language),
        ...monthGrid(year, month),
    }))
    const selectable = months.flatMap((m) => m.days).filter((iso) => iso >= today)
    const anchor = pick === 'end' && end ? end : start
    const activeIso = selectable.includes(active)
        ? active
        : selectable.find((iso) => iso === anchor) || selectable.find((iso) => iso === today) || selectable[0]

    useEffect(() => {
        if (!focusIso) return
        setActive(focusIso)
        dayRefs.current[focusIso]?.focus()
    }, [focusIso, focusKey])

    const now = new Date()
    const prevDisabled = view.year * 12 + view.month <= now.getFullYear() * 12 + now.getMonth()
    const previewOn = !!(start && !end && hover && hover > start)

    const dayClass = (iso) => {
        const classes = ['cev-day']
        if (iso === start) classes.push('start')
        if (end && iso === end) classes.push('end')
        if (start && end && iso > start && iso < end) classes.push('in')
        if (iso === today) classes.push('today')
        if (previewOn && iso > start && iso < hover) classes.push('hov')
        if (previewOn && iso === start) classes.push('hstart')
        if (previewOn && iso === hover) classes.push('hend')
        return classes.join(' ')
    }

    const handleKeyDown = (event, iso) => {
        const step = ARROW_STEP[event.key]
        if (!step) return
        event.preventDefault()
        const next = addDays(iso, step)
        if (next < today) return
        const target = dayRefs.current[next]
        if (target) {
            setActive(next)
            target.focus()
        } else {
            onShift(step > 0 ? 1 : -1, next)
        }
    }

    const dows = weekdayLabels(language)

    return (
        <div className="cev-cal" role="group" aria-labelledby={labelledBy}>
            <div className="cev-cal-head">
                <button
                    type="button"
                    className="cev-nav"
                    disabled={prevDisabled}
                    aria-label={t('Previous month')}
                    onClick={() => onShift(-1)}
                >
                    <ChevronLeftIcon />
                </button>
                <p className="cev-cal-hint" aria-live="polite">
                    {hint}
                </p>
                <button type="button" className="cev-nav" aria-label={t('Next month')} onClick={() => onShift(1)}>
                    <ChevronRightIcon />
                </button>
            </div>

            <div className="cev-months" onMouseLeave={() => setHover('')}>
                {months.map((month) => (
                    <div className="cev-month" key={month.key}>
                        <b>{month.title}</b>
                        <div className="cev-grid">
                            {dows.map((dow, i) => (
                                <span className="cev-dow" aria-hidden="true" key={`dow-${i}`}>
                                    {dow}
                                </span>
                            ))}
                            {Array.from({ length: month.leading }, (_, i) => (
                                <span key={`blank-${i}`} />
                            ))}
                            {month.days.map((iso, index) => {
                                const disabled = iso < today
                                return (
                                    <button
                                        type="button"
                                        key={iso}
                                        ref={(element) => {
                                            if (element && !disabled) dayRefs.current[iso] = element
                                            else delete dayRefs.current[iso]
                                        }}
                                        className={dayClass(iso)}
                                        disabled={disabled}
                                        tabIndex={iso === activeIso ? 0 : -1}
                                        aria-label={formatDate(iso, language)}
                                        aria-pressed={iso === start || iso === end}
                                        onClick={() => onChoose(iso)}
                                        onMouseEnter={() => setHover(iso)}
                                        onFocus={() => setHover(iso)}
                                        onKeyDown={(event) => handleKeyDown(event, iso)}
                                    >
                                        <span>{index + 1}</span>
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                ))}
            </div>

            <div className="cev-cal-foot">
                {start && (
                    <button type="button" className="cev-clear" onClick={onClear}>
                        {t('Clear dates')}
                    </button>
                )}
            </div>
        </div>
    )
}
